import { useEffect, useState } from 'react';

/**
 * Preferência de tema do ambiente autenticado (R-UX02-P01/D-101).
 *
 * **Ausência significa seguir o sistema.** `preference` é `null` até a pessoa tocar
 * no toggle uma vez — antes disso, `effective` (o que de fato se aplica) segue
 * `prefers-color-scheme` do sistema operacional, e continua seguindo em tempo real
 * (o listener de `matchMedia` abaixo). Depois do primeiro toque, a preferência vira
 * EXPLÍCITA e passa a valer mesmo que o sistema mude — é o comportamento do próprio
 * baseline aprovado (`docs/design/home/_base.css`, dupla `@media` / `[data-theme]`).
 *
 * **Namespaced e tolerante à ausência de storage.** A chave carrega o produto
 * (`argink.web`) para não colidir com nada que um dia compartilhe `localStorage`
 * neste domínio, e cada leitura/escrita está em `try/catch`: modo privado, política
 * do navegador ou quota estourada não podem quebrar o toggle — só o degradam para
 * "não lembra entre sessões", nunca para uma tela quebrada.
 *
 * **Escopo:** este módulo só resolve QUAL string ('light'/'dark') está em vigor.
 * Quem aplica isso visualmente é `AppShell` (prop `theme`), que grava `data-theme`
 * só na sua própria raiz (`.argink-app-shell`) — a Home pública e o Login não
 * renderizam `AppShell`, então nunca veem esta preferência.
 */

export type ThemePreference = 'light' | 'dark';

const STORAGE_KEY = 'argink.web.theme-preference';

function readStoredPreference(): ThemePreference | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === 'light' || raw === 'dark' ? raw : null;
  } catch {
    return null;
  }
}

function writeStoredPreference(value: ThemePreference): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Storage indisponível (privado, política do navegador, quota) — o toggle
    // continua funcionando nesta sessão, só não sobrevive a um reload.
  }
}

function prefersDarkMediaQuery(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  try {
    return window.matchMedia('(prefers-color-scheme: dark)');
  } catch {
    return null;
  }
}

export interface UseThemePreferenceResult {
  /** O tema em vigor AGORA — sempre resolvido, nunca `null`. É o que se passa
   * para `<AppShell theme={...}>`. */
  theme: ThemePreference;
  /** Alterna entre claro/escuro e GRAVA a escolha como preferência explícita
   * (o toque no toggle sempre sai do estado "seguir o sistema"). */
  toggle: () => void;
}

export function useThemePreference(): UseThemePreferenceResult {
  const [preference, setPreference] = useState<ThemePreference | null>(() =>
    readStoredPreference(),
  );
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => {
    const mql = prefersDarkMediaQuery();
    return mql?.matches ?? false;
  });

  // Só importa reagir à mudança do SISTEMA enquanto não houver preferência
  // explícita — uma vez que a pessoa escolheu, o sistema deixa de decidir por ela.
  useEffect(() => {
    const mql = prefersDarkMediaQuery();
    if (!mql) return undefined;
    const onChange = (event: MediaQueryListEvent) => setSystemPrefersDark(event.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const theme: ThemePreference = preference ?? (systemPrefersDark ? 'dark' : 'light');

  function toggle() {
    const next: ThemePreference = theme === 'dark' ? 'light' : 'dark';
    setPreference(next);
    writeStoredPreference(next);
  }

  return { theme, toggle };
}
